import { IActivity, ISection } from '@sdk/contracts';
import { Button, Divider, Flex, Popover, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { RemoveActivity } from '../RemoveActivity';
import { RemoveBaseContentActivity } from '../RemoveBaseContentActivity';
import { SyncedActivitySummary } from '../SyncedActivitySummary';
import { ToogleActivityActive } from '../ToogleActivityActive';
import { ToogleActivityPlanificable } from '../ToogleActivityPlanificable';
import { UnsyncActivityAction } from '../UnsyncActivityAction';

import { CheckpointWrapper, PopoverWrapper } from './ActivityActions.styles';

import { ActivityTranscription } from '@/components/ActivityTranscription';
import { Icon } from '@/components/Icon';
import { useProfile } from '@/store';
import { ECourseStep } from '@/types';
import { ECourseContentStep } from '@/types';

type Props = {
  section: ISection;
  activity: IActivity;
  target: ECourseContentStep;
};

export const ActivityActions: React.FC<Props> = ({ section, activity, target }) => {
  const { t } = useTranslation('CourseTranslations');
  const {
    roleCheck: { isManager }
  } = useProfile();
  const navigateTo = useNavigate();

  const hasMoreContent = useMemo(
    () => isManager && activity.questions && activity.questions.length > 0,
    [isManager, activity]
  );

  const handleGoToParentActivity = () => {
    navigateTo(
      `/content/base-content/${activity.parentCourse?._id}?activeTab=${ECourseStep.CONTENT}&contentTab=${target}&sectionId=${activity.parentSection?._id}`
    );
  };

  const shouldShowRemoveActivity = useMemo(() => {
    const hasSyncedActivities = (activity.syncedActivities ?? []).length > 0;
    if (hasSyncedActivities) return false;

    const isChildInSyncedSection = !!activity.parentActivity && !!section.parentSection;
    return !isChildInSyncedSection;
  }, [activity, section]);

  const shouldShowRemoveBaseContentActivity = useMemo(() => {
    return (activity.syncedActivities ?? []).length > 0;
  }, [activity]);

  const activityStatusContent = (
    <Flex vertical gap={16}>
      <ToogleActivityActive sectionId={section._id} activity={activity} />
      {isManager && <ToogleActivityPlanificable sectionId={section._id} activity={activity} />}
    </Flex>
  );

  const content = (
    <Flex vertical align="flex-start">
      {activity.parentActivity && isManager && (
        <>
          <Button
            type="text"
            icon={<Icon i="Renew" size={16} />}
            onClick={handleGoToParentActivity}>
            {t('GO_TO_PARENT_ACTIVITY')}
          </Button>

          <SyncedActivitySummary activity={activity} />

          {!section.parentSection && <UnsyncActivityAction activity={activity} />}
        </>
      )}

      <Popover placement="right" content={activityStatusContent} trigger="hover">
        <Button
          type="text"
          block
          style={{ textAlign: 'left' }}
          icon={<Icon i="CircleDash" size={16} />}>
          <Flex align="center" gap={16}>
            <span>{t('ACTIVITY_STATUS')}</span>
            <Icon i="ChevronRight" size={20} />
          </Flex>
        </Button>
      </Popover>

      {shouldShowRemoveActivity && (
        <RemoveActivity activity={activity} sectionId={section._id} buttonType="text" />
      )}

      {shouldShowRemoveBaseContentActivity && (
        <RemoveBaseContentActivity activity={activity} sectionId={section._id} buttonType="text" />
      )}

      {hasMoreContent && <Divider style={{ margin: 0 }} />}

      {isManager && <ActivityTranscription activity={activity} />}

      {activity.questions && activity.questions.length > 0 && (
        <Flex gap={8} justify="center">
          <Icon i="TestTool" color="green6" />
          <CheckpointWrapper>{t('ACTIVITY_RECAP_ACTIVE')}</CheckpointWrapper>
        </Flex>
      )}
    </Flex>
  );

  return (
    <Tooltip title={t('MORE_ACTIONS')}>
      <Popover
        placement="right"
        content={<PopoverWrapper>{content}</PopoverWrapper>}
        trigger="click">
        <Button type="text" icon={<Icon i="OverflowMenuVertical" size={20} />} />
      </Popover>
    </Tooltip>
  );
};
